import lodashGet from 'lodash/get'

export const makeDecimalValue = (value) => (value ? Number(value / 100).toFixed(2) : value)

export const makeValueWithoutDecimal = (value) => {
  if (value) {
    const newValue = Number(value * 100)
    return newValue || 0
  }
  return value
}

export const oneTimeEarningsMap = (arr) =>
  arr.map((item) => ({
    ...item,
    invoice_amount: makeDecimalValue(item.invoice_amount),
    user_earnings: makeDecimalValue(item.user_earnings),
  }))

const errorReading = (obj = {}, errorsList = []) => {
  Object.values(obj).forEach((item) => {
    if (typeof item === 'string' && item.length) {
      errorsList.push(item)
    } else if (typeof item === 'object') {
      errorReading(item, errorsList)
    }
  })
  return errorsList
}

export const errorHandler = ({ error, commit, state, message }) => {
  const defaultMessage = `Something went wrong in ${message}, please try again.`
  const errorObject = lodashGet(error, 'response.data', {})
  const errors = errorReading(errorObject)
  const errorsList = errors.length ? errors : [defaultMessage]
  const errorMessageList = state ? state.errorMessageList.concat(errorsList) : errorsList
  commit('setErrorMessageList', errorMessageList)
}

export const validURL = (str) => {
  const pattern = new RegExp(
    '^((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.,~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.,~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ) // fragment locator
  return !!pattern.test(str)
}

export const getCurrencySymbol = (curr) => {
  switch (curr) {
    case 'DKK':
      return 'kr'
    case 'EUR':
      return '€'
    case 'NOK':
      return 'kr'
    case 'PLN':
      return 'zł'
    case 'SEK':
      return 'kr'
    case 'GBP':
      return '£'
    case 'AUD':
      return '$'
    case 'USD':
      return '$'
    default:
      return curr
  }
}

export const formatCPABeforeSend = (value) => {
  return (Number(value).toFixed(0) / 100).toFixed(2)
}

/**
 * A wrapper function for vuex fetcher actions.
 *
 * Instead of adding try,catch block and changing the loading
 * error & success state in each function, call this wrapper instead
 * which update the status & calls the passed promise along with it's args.
 *
 *
 * Note that you need to do the following in order for this wrapper to work properly:
 *
 * 1-Make sure to have the following properties defined in your vuex state:
 * loading: false, error: "", success: "".
 *
 * 2- Add a mutation named "update" in the following format update(state, {path, value}).
 *
 * @param {Promise}  promise  A Function or any callable that returns a promise.
 * @param {Function}  commit  The Vuex commit function to change the states..
 * @param {Array}   args      Array of the args that you want to pass to the function,
 *                            Default value is empty array.
 * @param {Object} params  Options
 * @param {String} params.loadingPath the loading path default is "loading".
 * @param {String} params.errorPath the error path default is "error".
 * @param {String} params.successPath the success path default is "success".
 * @param {String} params.successMessage the success message default is "Success!".
 * @param {String} params.updateFunctionName the name of the function which used to update the status default is "update".
 *
 *
 * @return {Promise} Return the result of the passed promise.
 */
export const fetchWrapper = async (promise, commit, args = [], params) => {
  // Set default values,
  const loadingPath = params?.loadingPath || 'loading'
  const errorPath = params?.errorPath || 'error'
  const successPath = params?.successPath || 'success'
  const successMessage = params?.successMessage || ''
  const updateFunctionName = params?.updateFunctionName || 'update'
  try {
    commit(updateFunctionName, { path: loadingPath, value: true })
    commit(updateFunctionName, { path: errorPath, value: '' })
    commit(updateFunctionName, { path: successPath, value: '' })
    const result = await promise(...args)
    commit(updateFunctionName, { path: successPath, value: successMessage })
    return result
  } catch (error) {
    console.error(error)
    commit(updateFunctionName, { path: errorPath, value: error.message })
  } finally {
    commit(updateFunctionName, { path: loadingPath, value: false })
  }
}

export const countries = [
  { value: 'ALL', label: 'ALL' },
  { value: 'AU', label: 'Australia' },
  { value: 'BE', label: 'Belgium' },
  { value: 'CZ', label: 'Czechia' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'GB', label: 'Great Britain' },
  { value: 'IT', label: 'Italy' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'NO', label: 'Norway' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'RO', label: 'Romania' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'ES', label: 'Spain' },
  { value: 'SE', label: 'Sweden' },
  { value: 'US', label: 'United States' },
]

export const utmTags = {
  campaign: {
    value: undefined,
    label: 'Utm campaign',
    example: 'E.g 20%Off',
    name: 'campaign',
    code: 'utm_campaign',
  },
  medium: {
    value: undefined,
    label: 'Utm medium',
    example: 'E.g socialmedia',
    name: 'medium',
    code: 'utm_medium',
  },
  source: {
    value: undefined,
    label: 'Utm source',
    example: 'E.g metapic',
    name: 'source',
    code: 'utm_source',
  },
  content: {
    value: undefined,
    label: 'Utm Piece of Content',
    example: 'E.g sidebar',
    name: 'content',
    code: 'utm_content',
  },
  term: {
    value: undefined,
    label: 'Utm term',
    example: 'E.g comfortable+heels',
    name: 'term',
    code: 'utm_term',
  },
}
